import React, { useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import moment from "moment";
import { StatusCode } from "api/protocols";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan, faChevronDown, faEllipsisVertical, faEnvelope, faMagnifyingGlass, faPen, faPlus, faPrint } from "@fortawesome/pro-regular-svg-icons";

import Callout from "components/callout/Callout";
import ButtonGroup from "components/buttonGroup/ButtonGroup";
import Page from "components/page/Page";
import { ButtonNew } from "components/buttonNew";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import DateBar from "elements/teesheet/DateBar";
import { formatDate, capitalize } from "helpers/Helpers";

import { dequeue, enqueue, IUIActions, showError } from "redux/actions/ui";
import { ILocation, IModule, IReservationBooking, ISegment } from "redux/reducers/models/reservations";
import "./reservation.scss";
import ReservationBookingModalNew from "./ReservationBookingModalNew";
import ReservationBookingModalEdit from "./ReservationBookingModalEdit";
import { useTranslation } from "react-i18next";
import { unitLabel } from "../settings/reservations/unitLabel";
import { IAuthState } from "redux/reducers/auth";
import { useHistory } from "react-router";
import { Form } from "react-bootstrap";
import FormLayout from "components/form/FormLayout";
import Sheet from "components/sheet/Sheet";
import Input from "components/form/input/Input";
import Tabs from "components/tabs/Tabs";
import Checkbox from "components/form/checkbox/Checkbox";
import TextEditor from "components/textEditor/textEditor";
import { formatMarkdownBreaks } from "components/markdown/Markdown";
import BookedSegment from "./reservationDragAndDropElements/BookedSegment";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import OpenSegment, { SegmentSlotFlags } from "./reservationDragAndDropElements/OpenSegment";
import RightClickMenu from "components/rightClickMenu/RightClickMenu";
import AuditTrailNote from "components/bookingPopUp/auditTrailNote/AuditTrailNote";
import Popup from "components/popup/Popup";
import Spin from "components/spin/spin";
import {
  DeleteReservationSegment,
  GetPrintReservation,
  GetReservationBooking,
  GetReservationLocation,
  GetReservationModuleAll,
  GetReservationNotificationAll,
  GetReservationSegment,
  GetSegmentAuditTrail,
  IReservationNotification,
  MoveReservationBooking,
  PostReservationEmail,
  PutAcceptReservationBooking,
  PutCancelReservationBooking,
  PutReservationBooking,
  PutReservationSegment,
} from "api/rpc/2024-04/facilityAdmin/reservation/reservation";
import axios, { CancelToken } from "axios";
import teeSheetToast from "components/teeSheetToast/TeeSheetToast";
import { useAppDispatch } from "hooks/redux";
import { TCustomerNote } from "../customer/context/contextTypes";
import { GetCustomerNotes } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import Portal from "elements/Portal";
import useModal from "hooks/modals/useModal";
import { loadActiveTableNew } from "redux/actions/cart";
import ReservationTableSelection from "./reservationTableSelection/ReservationTableSelection";
import { GetTable, PutOpenTable } from "api/rpc/2024-04/facilityAdmin/tables/tables";
import { PostLineItemToCart } from "api/rpc/2024-04/facilityAdmin/order/cart/lineItem";

interface IExtendedSegment extends ISegment {
  checked?: boolean;
}

interface IState {
  modules: Array<IModule>;
  selectedModule: IModule;
  selectedDate: Date;
  selectedLocation: ILocation;
  selectedSegment: ISegment;
  durations: { [key: string]: Array<number> };
  durationKey: string;
  bookingSegments: Array<ISegment>;
  locations: Array<ILocation>;
  segments: { [key: string]: Array<ISegment> };
  bookedSegments: Array<ISegment>;
  uniqueBookedSegments: Array<IExtendedSegment>;
  newBookingActive: boolean;
  editBookingActive: boolean;
  draggingBookingId: number;
  auditTrails: any;
  auditTrailModalVisible: boolean;
  auditTrailsLoading: boolean;
  deleteSegmentPopupVisible: boolean;
  segmentId: number;
  notifications: IReservationNotification[];
  booking: IReservationBooking;
}

interface IEmailState {
  modalVisible: boolean;
  subject: string;
  content: string;
  recipients: number[];
  selectAllRecipients: boolean;
}

interface IProps {
  uiActions: IUIActions;
  authStore: IAuthState;
}

const LOCATION_GROUP_SIZE = 4;
const SEGMENT_TIME_COLUMN_WIDTH = 132;

function groupLocations(locations: Array<ILocation> | null) {
  if (!locations || locations.length <= LOCATION_GROUP_SIZE) {
    return null;
  }

  const groups: Array<Array<ILocation>> = [];
  for (let index = 0; index < locations.length; index += LOCATION_GROUP_SIZE) {
    groups.push(locations.slice(index, index + LOCATION_GROUP_SIZE));
  }
  return groups;
}

function locationGroupLabel(group: Array<ILocation>) {
  const first = group[0]?.title ?? "";
  const last = group[group.length - 1]?.title ?? "";
  const firstMatch = first.match(/^(.*?)(\d+)\s*$/);
  const lastMatch = last.match(/^(.*?)(\d+)\s*$/);

  if (firstMatch && lastMatch && firstMatch[1].trim() === lastMatch[1].trim()) {
    const prefix = firstMatch[1].trim();
    return prefix ? `${prefix} ${firstMatch[2]}–${lastMatch[2]}` : `${firstMatch[2]}–${lastMatch[2]}`;
  }

  return first === last ? first : `${first}–${last}`;
}

function formatSlotRange(startTime: string, span: number) {
  const start = moment(startTime, ["HH:mm:ss", "HH:mm"]);
  const end = start.clone().add(span * 30, "minutes");
  return `${start.format("hh:mm A")} – ${end.format("hh:mm A")}`;
}

function eventSegmentAt(
  segments: { [key: string]: Array<ISegment> } | null | undefined,
  locationId: number,
  segmentKey?: string,
) {
  if (!segmentKey) {
    return undefined;
  }
  return segments?.[segmentKey]?.find(entry => entry.location_id === locationId);
}

function eventBounds(
  segments: { [key: string]: Array<ISegment> } | null | undefined,
  segment: ISegment,
  locationId: number,
  segmentKey: string,
) {
  const keys = Object.keys(segments ?? {});
  const index = keys.indexOf(segmentKey);
  const previous = eventSegmentAt(segments, locationId, keys[index - 1]);
  let first = segment;
  let last = segment;
  let runEndIndex = index;

  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    const item = eventSegmentAt(segments, locationId, keys[cursor]);
    if (!item || !sameScheduleEvent(segment, item)) {
      break;
    }
    first = item;
  }

  for (let cursor = index + 1; cursor < keys.length; cursor += 1) {
    const item = eventSegmentAt(segments, locationId, keys[cursor]);
    if (!item || !segmentsShareRun(segment, item)) {
      break;
    }
    runEndIndex = cursor;
  }

  for (let cursor = runEndIndex + 1; cursor < keys.length; cursor += 1) {
    const item = eventSegmentAt(segments, locationId, keys[cursor]);
    if (!item || !sameScheduleEvent(segment, item)) {
      break;
    }
    last = item;
  }

  let hasLaterOpen = false;
  for (let cursor = runEndIndex + 1; cursor < keys.length; cursor += 1) {
    const item = eventSegmentAt(segments, locationId, keys[cursor]);
    if (!item || !sameScheduleEvent(segment, item)) {
      break;
    }
    if (item.blocked_type === "open" && !item.booking_id) {
      hasLaterOpen = true;
      break;
    }
  }

  const start = moment(first.start_time, ["HH:mm:ss", "HH:mm"]);
  const end = moment(last.start_time, ["HH:mm:ss", "HH:mm"]).add(30, "minutes");

  return {
    continuesBefore: sameScheduleEvent(segment, previous),
    showTime: !hasLaterOpen,
    timeLabel: start.isValid() ? `${start.format("hh:mm A")} – ${end.format("hh:mm A")}` : "",
  };
}

function eventHasBooking(
  segments: { [key: string]: Array<ISegment> } | null | undefined,
  segment: ISegment,
  locationId: number,
) {
  return Object.values(segments ?? {}).some(row => {
    const item = row?.find(entry => entry.location_id === locationId);
    return Boolean(item?.booking_id && sameScheduleEvent(segment, item));
  });
}

function slotFlagsForSpan(
  segments: { [key: string]: Array<ISegment> } | null | undefined,
  locationId: number,
  segmentKey: string,
  span: number,
) {
  const keys = Object.keys(segments ?? {});
  const index = keys.indexOf(segmentKey);
  const flags = [];
  for (let offset = 0; offset < span; offset += 1) {
    const item = segments?.[keys[index + offset]]?.find(entry => entry.location_id === locationId);
    flags.push({
      webBooking: Boolean(item?.web_booking_enabled),
      creditCard: Boolean(item?.credit_card_required),
    });
  }
  return flags;
}

function bookingMatchesQuery(segment: ISegment, query: string) {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return true;
  }
  if (!segment.booking) {
    return false;
  }

  const booking = segment.booking;
  const participantNames: Array<string | undefined> = [];
  booking.participants?.forEach(participant => {
    participantNames.push(participant.full_name, participant.first_name, participant.last_name);
  });
  const values = [
    booking.customer?.full_name,
    booking.first_name,
    booking.last_name,
    booking.order_name,
    booking.confirmation_number,
    ...participantNames,
  ];

  return values.some(value => value?.toLowerCase().includes(normalized));
}

function smoothScrollWithin(container: HTMLElement, target: HTMLElement, scrollLeft = container.scrollLeft) {
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const nextTop = container.scrollTop + targetRect.top - containerRect.top - Math.max(0, (container.clientHeight - targetRect.height) / 2);
  const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
  const maxLeft = Math.max(0, container.scrollWidth - container.clientWidth);
  const endTop = Math.min(Math.max(0, nextTop), maxTop);
  const endLeft = Math.min(Math.max(0, scrollLeft), maxLeft);
  const startTop = container.scrollTop;
  const startLeft = container.scrollLeft;
  const duration = 450;
  const startTime = performance.now();

  function step(now: number) {
    const progress = Math.min(1, (now - startTime) / duration);
    const eased = 1 - Math.pow(1 - progress, 3);
    container.scrollTop = startTop + (endTop - startTop) * eased;
    container.scrollLeft = startLeft + (endLeft - startLeft) * eased;
    if (progress < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

function segmentsShareRun(current: ISegment, other?: ISegment) {
  if (!other) {
    return false;
  }

  if (
    current.blocked_type === "booked" &&
    current.booking_id &&
    other.blocked_type === "booked" &&
    other.booking_id === current.booking_id
  ) {
    return true;
  }

  if (current.blocked_type === "blocked" && other.blocked_type === "blocked") {
    return true;
  }

  const currentIsBand = current.blocked_type === "open" && !current.booking_id && (!!current.league_id || !!current.tournament_id);
  const otherIsBand = other.blocked_type === "open" && !other.booking_id && (!!other.league_id || !!other.tournament_id);
  if (!currentIsBand || !otherIsBand) {
    return false;
  }

  if (current.league_id && other.league_id === current.league_id) {
    return true;
  }

  return !current.league_id && !other.league_id && !!current.tournament_id && other.tournament_id === current.tournament_id;
}

function sameScheduleEvent(current?: ISegment, other?: ISegment) {
  if (!current || !other) {
    return false;
  }

  if (current.league_id && other.league_id === current.league_id) {
    return true;
  }

  return !current.league_id && !other.league_id && !!current.tournament_id && other.tournament_id === current.tournament_id;
}

export default function Reservation(props: IProps) {
  const { uiActions, authStore } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const {state: openTableModal, updateModal: updateOpenTableModal, closeModal: closeOpenTableModal} = useModal<{tableId: number}>({tableId: null});
  const {state: tableSelectionModal, updateModal: updateTableSelectionModal, closeModal: closeTableSelectionModal} = useModal<{tableId: number; guestsAmount: number; segment: ISegment}>({tableId: null, guestsAmount: 1, segment: null});
  const {
    state: approvalModal,
    updateModal: updateApprovalModal,
    closeModal: closeApprovalModal,
  } = useModal<{ segment: ISegment; action: "approve" | "decline" }>({ segment: null, action: "approve" });

  const MINUTES_PER_SEGMENT = 30;

  // eslint-disable-next-line no-unused-vars
  const [_highPriorityNotes, setHighPriorityNotes] = useState<TCustomerNote[]>([]);
  const [state, setState] = useState<IState>({
    modules: [],
    locations: [],
    segments: null,
    bookedSegments: null,
    uniqueBookedSegments: null,
    selectedModule: null,
    selectedDate: new Date(),
    selectedLocation: null,
    selectedSegment: null,
    durations: null,
    durationKey: "",
    bookingSegments: null,
    newBookingActive: false,
    editBookingActive: false,
    draggingBookingId: null,
    auditTrails: null,
    auditTrailModalVisible: false,
    auditTrailsLoading: false,
    deleteSegmentPopupVisible: false,
    segmentId: null,
    notifications: [],
    booking: null,
  });

  const [emailState, setEmailState] = useState<IEmailState>({
    modalVisible: false,
    subject: "",
    content: "",
    recipients: [],
    selectAllRecipients: false,
  });

  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState("");
  const [locationGroupIndex, setLocationGroupIndex] = useState(0);
  const [moduleMenuOpen, setModuleMenuOpen] = useState(false);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const moduleMenuRef = useRef<HTMLDivElement>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const segmentsContainerRef = useRef<HTMLDivElement>(null);
  const searchScrollKeyRef = useRef("");
  const blockedMenuSegmentRef = useRef<ISegment | null>(null);
  const locationScrollTargetRef = useRef<number | null>(null);
  const [blockedMenuSegment, setBlockedMenuSegment] = useState<ISegment | null>(null);
  const [locationColumnWidth, setLocationColumnWidth] = useState<number | null>(null);
  const locationGroups = useMemo(() => groupLocations(state.locations), [state.locations]);
  const activeLocationGroupIndex = locationGroups ? Math.min(locationGroupIndex, locationGroups.length - 1) : 0;

  useEffect(() => {
    const query = searchQuery.trim();
    if (!query || !state.segments || !state.locations?.length) {
      searchScrollKeyRef.current = "";
      return;
    }

    let matchBookingId: number | null = null;
    let matchLocationId: number | null = null;
    const keys = Object.keys(state.segments);

    for (const segmentKey of keys) {
      for (const location of state.locations) {
        const segment = state.segments[segmentKey]?.find(item => item.location_id === location.id);
        if (!segment || segment.blocked_type !== "booked" || !segment.booking_id || !bookingMatchesQuery(segment, query)) {
          continue;
        }
        const index = keys.indexOf(segmentKey);
        const previous = index > 0
          ? state.segments[keys[index - 1]]?.find(item => item.location_id === location.id)
          : undefined;
        if (segmentsShareRun(segment, previous)) {
          continue;
        }
        matchBookingId = segment.booking_id;
        matchLocationId = location.id;
        break;
      }
      if (matchBookingId) {
        break;
      }
    }

    if (!matchBookingId) {
      return;
    }

    const needsColumnLayout = state.locations.length > LOCATION_GROUP_SIZE;
    if (needsColumnLayout && !locationColumnWidth) {
      return;
    }

    const scrollKey = `${query}:${matchBookingId}:${locationColumnWidth ?? "fit"}`;
    if (searchScrollKeyRef.current === scrollKey) {
      return;
    }

    const container = segmentsContainerRef.current;
    const card = container?.querySelector<HTMLElement>(`[data-booking-id="${matchBookingId}"]`);
    if (!container || !card) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const timeColumn = container.querySelector<HTMLElement>(".segment-time");
    const timeWidth = timeColumn?.getBoundingClientRect().width || SEGMENT_TIME_COLUMN_WIDTH;
    const visibleBayWidth = Math.max(0, container.clientWidth - timeWidth);
    const locationIndex = state.locations.findIndex(location => location.id === matchLocationId);
    const columnWidth = locationColumnWidth ?? 0;
    const columnLeft = columnWidth > 0 && locationIndex >= 0 ? locationIndex * columnWidth : null;
    const horizontallyOut = columnLeft === null
      ? cardRect.right <= containerRect.left + timeWidth || cardRect.left >= containerRect.right
      : columnLeft + columnWidth <= container.scrollLeft + 1 || columnLeft >= container.scrollLeft + visibleBayWidth - 1;
    const verticallyOut = cardRect.bottom <= containerRect.top || cardRect.top >= containerRect.bottom;
    if (!horizontallyOut && !verticallyOut) {
      searchScrollKeyRef.current = scrollKey;
      return;
    }

    searchScrollKeyRef.current = scrollKey;
    locationScrollTargetRef.current = null;
    const nextLeft = horizontallyOut && columnLeft !== null
      ? columnLeft - Math.max(0, (visibleBayWidth - columnWidth) / 2)
      : container.scrollLeft;
    smoothScrollWithin(container, card, nextLeft);
  }, [searchQuery, state.segments, state.locations, locationColumnWidth]);

  useEffect(() => {
    function closeMenus(event: MouseEvent) {
      if (moduleMenuRef.current && !moduleMenuRef.current.contains(event.target as Node)) {
        setModuleMenuOpen(false);
      }
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
        setActionsMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", closeMenus);
    return () => document.removeEventListener("mousedown", closeMenus);
  }, []);

  useEffect(() => {
    setLocationGroupIndex(0);
    segmentsContainerRef.current?.scrollTo({ left: 0 });
  }, [state.selectedModule?.id]);

  useEffect(() => {
    const container = segmentsContainerRef.current;
    if (!container || !locationGroups) {
      setLocationColumnWidth(null);
      return;
    }

    function measure() {
      const timeCell = container.querySelector<HTMLElement>(".segment-time");
      const timeWidth = timeCell?.getBoundingClientRect().width || SEGMENT_TIME_COLUMN_WIDTH;
      const nextWidth = (container.clientWidth - timeWidth) / LOCATION_GROUP_SIZE;
      setLocationColumnWidth(current => (current !== null && Math.abs(current - nextWidth) < 0.5 ? current : nextWidth));
    }

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [locationGroups, state.segments, state.locations]);

  useEffect(() => {
    const container = segmentsContainerRef.current;
    if (!container || !locationGroups || !locationColumnWidth || !state.locations?.length) {
      return;
    }

    function releaseLocationScrollTarget() {
      locationScrollTargetRef.current = null;
    }

    function syncLocationGroup() {
      if (locationScrollTargetRef.current !== null) {
        const maxLeft = Math.max(0, container.scrollWidth - container.clientWidth);
        const targetLeft = Math.min(
          maxLeft,
          locationScrollTargetRef.current * LOCATION_GROUP_SIZE * locationColumnWidth,
        );
        if (Math.abs(container.scrollLeft - targetLeft) > 2) {
          return;
        }
        locationScrollTargetRef.current = null;
      }

      const maxLeft = Math.max(0, container.scrollWidth - container.clientWidth);
      const atEnd = maxLeft > 0 && container.scrollLeft >= maxLeft - 1;
      const leftIndex = Math.min(
        state.locations.length - 1,
        Math.max(0, Math.floor((container.scrollLeft + locationColumnWidth / 2) / locationColumnWidth)),
      );
      const groupIndex = atEnd
        ? locationGroups.length - 1
        : Math.min(locationGroups.length - 1, Math.floor(leftIndex / LOCATION_GROUP_SIZE));
      setLocationGroupIndex(current => (current === groupIndex ? current : groupIndex));
    }

    container.addEventListener("scroll", syncLocationGroup, { passive: true });
    container.addEventListener("wheel", releaseLocationScrollTarget, { passive: true });
    container.addEventListener("pointerdown", releaseLocationScrollTarget);
    return () => {
      container.removeEventListener("scroll", syncLocationGroup);
      container.removeEventListener("wheel", releaseLocationScrollTarget);
      container.removeEventListener("pointerdown", releaseLocationScrollTarget);
    };
  }, [locationGroups, locationColumnWidth, state.locations]);

  useEffect(() => {
    if (!state.draggingBookingId) {
      return;
    }

    const container = segmentsContainerRef.current;
    if (!container) {
      return;
    }

    locationScrollTargetRef.current = null;
    const edgeSize = 72;
    let pointerX: number | null = null;
    let pointerY: number | null = null;
    let frame = 0;

    function scrollWhileDragging() {
      frame = requestAnimationFrame(scrollWhileDragging);
      if (pointerX === null || pointerY === null) {
        return;
      }

      const rect = container.getBoundingClientRect();
      const withinVertical = pointerY >= rect.top && pointerY <= rect.bottom;
      if (!withinVertical) {
        return;
      }

      const timeColumn = container.querySelector<HTMLElement>(".segment-time");
      const leftEdge = timeColumn?.getBoundingClientRect().right ?? rect.left;
      const distanceFromLeft = pointerX - leftEdge;
      const distanceFromRight = rect.right - pointerX;
      let delta = 0;
      if (pointerX >= rect.left && distanceFromLeft < edgeSize) {
        const intensity = 1 - Math.max(0, distanceFromLeft) / edgeSize;
        delta = -Math.ceil(4 + intensity * 14);
      } else if (distanceFromRight >= 0 && distanceFromRight < edgeSize) {
        const intensity = 1 - distanceFromRight / edgeSize;
        delta = Math.ceil(4 + intensity * 14);
      }

      if (delta === 0) {
        return;
      }

      const maxLeft = Math.max(0, container.scrollWidth - container.clientWidth);
      const nextLeft = Math.min(maxLeft, Math.max(0, container.scrollLeft + delta));
      if (nextLeft !== container.scrollLeft) {
        container.scrollLeft = nextLeft;
      }
    }

    function handleDragOver(event: DragEvent) {
      pointerX = event.clientX;
      pointerY = event.clientY;
    }

    document.addEventListener("dragover", handleDragOver);
    frame = requestAnimationFrame(scrollWhileDragging);
    return () => {
      document.removeEventListener("dragover", handleDragOver);
      cancelAnimationFrame(frame);
    };
  }, [state.draggingBookingId]);

  useEffect(() => {
    void loadModules();
  }, []);

  useEffect(() => {
    if (state.selectedModule) {
      void loadModuleChange();
    }
  }, [state.selectedModule]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (state.selectedModule) {
      void loadSegments();
      void loadHighPriorityCustomerNotes(source.token);
    }
  }, [state.selectedDate]);

  async function loadModules() {
    const moduleRes = await GetReservationModuleAll(null, true);
    if (moduleRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation.001"));
      return;
    }
    setState(prevState => ({ ...prevState, modules: moduleRes.data, selectedModule: moduleRes?.data[0] }));
  }

  async function loadHighPriorityCustomerNotes(token?: CancelToken) {
    const bookedSegments = state.bookedSegments;
    const customerNotes: TCustomerNote[] = [];

    for (const bookedSegment of bookedSegments) {
      if (bookedSegment?.booking?.customer?.has_high_priority_notes) {
        const highPriorityNotesRes = await GetCustomerNotes(
          {
            customer_id: bookedSegment.booking?.customer_id,
            high_priority: true,
          },
          true,
          token,
        );

        if (token && token.reason) {
          return;
        }
        if (highPriorityNotesRes.status === StatusCode.OK) {
          customerNotes.push(highPriorityNotesRes.data[0]);
        }
      }
    }
    setHighPriorityNotes(customerNotes);
  }

  async function loadModuleChange() {
    uiActions.enqueue();
    const [locationRes, segmentRes] = await Promise.all([
      GetReservationLocation(
        { facility_id: state?.selectedModule?.facility_id, reservation_module_id: state?.selectedModule?.id },
        false,
      ),
      GetReservationSegment(
        { module_id: state?.selectedModule?.id, date: moment(state.selectedDate).format("YYYY-MM-DD"), extended: true },
        false,
      ),
    ]);

    if (locationRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation.002"));
      uiActions.dequeue();
      return;
    }

    if (segmentRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation.003"));
      uiActions.dequeue();
      return;
    }
    uiActions.dequeue();

    const formattedSegments = segmentRes.data?.reduce(
      (formattedSegments: { [key: string]: Array<ISegment> }, currentSegment: ISegment) => {
        return {
          ...formattedSegments,
          [currentSegment.start_time]: formattedSegments[currentSegment.start_time]
            ? [...formattedSegments[currentSegment.start_time], currentSegment]
            : [currentSegment],
        };
      },
      {},
    );

    const bookedSegments = segmentRes.data?.filter((segment: ISegment) => segment.blocked_type === "booked");

    //filter out duplicate bookings
    const seenIds = new Set();
    const uniqueBookedSegments = bookedSegments
      .filter((segment: ISegment) => {
        const duplicateBooking = seenIds.has(segment.booking_id);
        seenIds.add(segment.booking_id);
        return !duplicateBooking;
      })
      .sort((a: ISegment, b: ISegment) => Number(b.booking?.status === "pending") - Number(a.booking?.status === "pending"));

    setState(prevState => ({
      ...prevState,
      locations: locationRes?.data,
      segments: formattedSegments,
      bookedSegments: bookedSegments,
      uniqueBookedSegments: uniqueBookedSegments,
    }));
  }

  async function loadSegments(useGlobalLoader = true) {
    const segmentRes = await GetReservationSegment(
      { module_id: state?.selectedModule?.id, date: moment(state.selectedDate).format("YYYY-MM-DD"), extended: true },
      useGlobalLoader,
    );
    if (segmentRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation.004"));
      return;
    }
    const formattedSegments = segmentRes.data?.reduce(
      (formattedSegments: { [key: string]: Array<ISegment> }, currentSegment: ISegment) => {
        return {
          ...formattedSegments,
          [currentSegment.start_time]: formattedSegments[currentSegment.start_time]
            ? [...formattedSegments[currentSegment.start_time], currentSegment]
            : [currentSegment],
        };
      },
      {},
    );

    const bookedSegments = segmentRes.data?.filter((segment: ISegment) => segment.blocked_type === "booked");

    //filter out duplicate bookings
    const seenIds = new Set();
    const uniqueBookedSegments = bookedSegments
      .filter((segment: ISegment) => {
        const duplicateBooking = seenIds.has(segment.booking_id);
        seenIds.add(segment.booking_id);
        return !duplicateBooking;
      })
      .sort((a: ISegment, b: ISegment) => Number(b.booking?.status === "pending") - Number(a.booking?.status === "pending"));

    setState(prevState => ({
      ...prevState,
      segments: formattedSegments,
      bookedSegments: bookedSegments,
      uniqueBookedSegments: uniqueBookedSegments,
    }));
  }

  async function loadSegmentAuditTrail(segmentId: number) {
    setState(prevState => ({
      ...prevState,
      auditTrailModalVisible: true,
      auditTrails: null,
      auditTrailsLoading: true,
    }));

    const auditRes = await GetSegmentAuditTrail({ segment_id: segmentId }, false);

    if (auditRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation.039"));
      return;
    }

    setState(prevState => ({
      ...prevState,
      auditTrails: auditRes.data,
      auditTrailsLoading: false,
    }));
  }

  async function updateCheckInStatus(status: string, segment: ISegment) {
    const segmentIds = state.bookedSegments
      .filter(bookedSegment => bookedSegment.booking_id === segment.booking_id)
      .map(bookedSegment => bookedSegment.id);

    const bookingRes = await PutReservationBooking(
      {
        id: segment.booking_id,
        segment_ids: segmentIds,
        date: segment.date,
        quantity: segment.booking.quantity,
        customer_payment_method_id: segment.booking.customer_payment_method_id,
        check_in_status: status,
      },
      true,
    );

    if (bookingRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation.040"));
      return;
    }

    void loadSegments();
  }

  async function updateFinancialStatus(status: string, segment: ISegment) {
    const segmentIds = state.bookedSegments
      .filter(bookedSegment => bookedSegment.booking_id === segment.booking_id)
      .map(bookedSegment => bookedSegment.id);

    const bookingRes = await PutReservationBooking(
      {
        id: segment.booking_id,
        segment_ids: segmentIds,
        date: segment.date,
        quantity: segment.booking.quantity,
        customer_payment_method_id: segment.booking.customer_payment_method_id,
        order_financial_status: status,
      },
      true,
    );

    if (bookingRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation.040"));
      return;
    }

    void loadSegments();
  }

  async function acceptBooking(segment: ISegment) {
    const bookingRes = await PutAcceptReservationBooking({ id: segment.booking_id }, true);

    if (bookingRes.status !== StatusCode.OK) {
      uiActions.showError(typeof bookingRes?.data === "string" ? bookingRes.data : t("Error approving booking request"));
      return;
    }

    uiActions.showSuccess(t("Booking request approved"));
    closeApprovalModal();
    void loadSegments();
  }

  async function declineBooking(segment: ISegment) {
    const bookingRes = await PutCancelReservationBooking(
      { id: segment.booking_id, send_cancellation_email: true },
      true,
    );

    if (bookingRes.status !== StatusCode.OK) {
      uiActions.showError(typeof bookingRes?.data === "string" ? bookingRes.data : t("Error declining booking request"));
      return;
    }

    uiActions.showSuccess(t("Booking request declined"));
    closeApprovalModal();
    void loadSegments();
  }

  function handleFilterMenuChange(module: IModule) {
    setState(prevState => ({ ...prevState, selectedModule: module }));
  }

  function handleDateChange(selectedDate: Date) {
    setState(prevState => ({ ...prevState, selectedDate }));
  }

  function openBookingModal(segment: ISegment, location: ILocation) {
    setState(prevState => ({
      ...prevState,
      selectedLocation: location,
      selectedSegment: segment,
      bookingSegments: null,
      newBookingActive: true,
    }));
  }

  async function openEditBookingModal(segment: ISegment, location: ILocation) {

    dispatch(enqueue());
    const [bookingRes, segmentsRes] = await Promise.all([GetReservationBooking({id: segment?.booking_id, extended: true}, false), GetReservationSegment(
      {
        module_id: state?.selectedModule?.id,
        date: segment?.booking?.date,
        start_time: segment?.booking?.start_time,
        location_id: location?.id,
      },
      false,
    )]);
    dispatch(dequeue());

    if (bookingRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting booking")));
      return;
    }

    if (segmentsRes?.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation.006"));
      return;
    }

    void setDurationsEdit(segmentsRes?.data, segment?.booking_id);
    setState(prevState => ({ ...prevState, selectedLocation: location, selectedSegment: segment, booking: bookingRes?.data }));
  }

  function setDurationsEdit(segments: Array<ISegment>, booking_id: number) {
    //We can only select segments from index 0-9 for 0.5 - 5 hours with 0.5 hour increments
    const max = Math.min(10, segments?.length);
    let currentKey = "";
    let minutes = 0;
    let hours = 0;

    const durations: { [key: string]: Array<number> } = {};

    for (let i = 0; i < max; i++) {
      minutes += MINUTES_PER_SEGMENT;
      if (
        (segments[i]?.blocked_type === "open" && segments[i]?.booking_id === null) ||
        (segments[i]?.blocked_type === "booked" && segments[i]?.booking_id === booking_id)
      ) {
        hours = minutes / 60;
        const timeKey = `${hours} ${hours > 1 ? "Hours" : "Hour"}`;
        //Set the durations object keys as the hours and the segment ids array as the value
        durations[timeKey] = segments
          ?.filter((filteredSegment: ISegment, index: number) => index <= i)
          ?.map((segment: ISegment) => segment?.id);
        if (segments[i]?.blocked_type === "booked" && segments[i]?.booking_id === booking_id) {
          currentKey = timeKey;
        }
      } else {
        break;
      }
    }

    //If no duration times are available to select, close the booking modal
    if (Object.keys(durations)?.length === 0) {
      uiActions.showError(t("secure.facility.reservation.reservation.009"));
      setState(prevState => ({
        ...prevState,
        selectedLocation: null,
        selectedSegment: null,
        editBookingActive: false,
      }));
      return;
    }

    setState(prevState => ({
      ...prevState,
      durations,
      durationKey: currentKey,
      editBookingActive: true,
      bookingSegments: segments,
    }));
  }

  function closeNewBooking() {
    setState(prevState => ({
      ...prevState,
      durations: null,
      selectedLocation: null,
      selectedSegment: null,
      newBookingActive: false,
    }));
  }

  function closeEditBooking() {
    setState(prevState => ({
      ...prevState,
      durations: null,
      selectedLocation: null,
      selectedSegment: null,
      durationKey: "",
      editBookingActive: false,
    }));
  }

  function toSegmentsEditPage() {
    history.push(`/admin/reservation/edit/${state.selectedModule?.id}?from=list`);
  }

  function navigateToSegmentConfiguration() {
    history.push(`/admin/settings/reservations/module/${state.selectedModule.id}`);
  }

  function navigateToModules() {
    history.push("/admin/settings/reservations/modules");
  }

  const hasModules = state.modules.length > 0;

  function handleOpenEmailModal() {
    setEmailState(prevState => ({ ...prevState, modalVisible: true }));
  }

  const tabs = [
    {
      id: "email-1",
      content: t("secure.facility.reservation.reservation.017"),
      panelID: "email-content-1",
    },

    {
      id: "recipients-1",
      content: t("secure.facility.reservation.reservation.018"),
      panelID: "recipients-content-1",
    },
  ];

  // Handle tab change
  const [selected, setSelected] = useState(0);
  const handleTabChange = (selectedTabIndex: number) => {
    setSelected(selectedTabIndex);
  };

  async function emailBookings() {
    const formattedDate = formatDate(state.selectedDate);

    const teeSheetRes = await PostReservationEmail(
      {
        date: formattedDate,
        subject: emailState.subject,
        content: formatMarkdownBreaks(emailState.content),
        booking_ids: emailState.recipients,
      },
      true,
    );

    if (teeSheetRes.status === StatusCode.OK) {
      uiActions.showSuccess(t("secure.facility.reservation.reservation.019"));
      setEmailState(prevState => ({
        ...prevState,
        subject: "",
        content: "",
        recipients: [],
        modalVisible: false,
        selectAllRecipients: false,
      }));
    } else {
      uiActions.showError(t("secure.facility.reservation.reservation.020"));
      return;
    }
  }

  function handleCheckboxChange(event: any, recipient_group: IExtendedSegment) {
    const chosenRecipients = emailState.recipients;

    state.uniqueBookedSegments.map(recipient => {
      if (recipient.booking.id === recipient_group.booking.id) {
        if (recipient.checked) {
          const index = chosenRecipients.indexOf(recipient_group.id);
          if (index > -1) {
            chosenRecipients.splice(index, 1);
          }

          return { ...recipient, checked: false };
        } else {
          chosenRecipients.push(recipient_group.booking.id);

          return { ...recipient, checked: true };
        }
      }

      return recipient;
    });

    setEmailState(prevState => ({
      ...prevState,
      recipients: chosenRecipients,
    }));
  }

  function handleChangeEmailSubject(e: React.ChangeEvent<HTMLInputElement>) {
    setEmailState(prevState => ({ ...prevState, subject: e.target.value }));
  }

  function handleChangeEmailContent(value: string) {
    setEmailState(prevState => ({ ...prevState, content: value }));
  }

  function handleSelectAllCheckboxChange(event: any) {
    const { checked } = event.target;

    const chosenRecipients = emailState.recipients;

    if (checked) {
      state.uniqueBookedSegments.map((segment: ISegment) => {
        chosenRecipients.push(segment.booking_id);
      });

      const filteredRecipients = [...new Set(chosenRecipients)];

      setEmailState(prevState => ({
        ...prevState,
        recipients: filteredRecipients,
        selectAllRecipients: checked,
      }));
    } else {
      setEmailState(prevState => ({
        ...prevState,
        recipients: [],
        selectAllRecipients: checked,
      }));
    }
  }

  function scrollToLocationGroup(index: number) {
    setLocationGroupIndex(index);
    const container = segmentsContainerRef.current;
    if (!container || !locationColumnWidth) {
      return;
    }

    const maxLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const left = Math.min(maxLeft, index * LOCATION_GROUP_SIZE * locationColumnWidth);
    if (Math.abs(container.scrollLeft - left) <= 2) {
      locationScrollTargetRef.current = null;
      return;
    }
    locationScrollTargetRef.current = index;
    container.scrollTo({ left, behavior: "smooth" });
  }

  function blockedSegmentFromPointer(
    event: React.PointerEvent<HTMLElement>,
    location: ILocation,
    startSegment: ISegment,
    span: number,
  ) {
    if (span <= 1) {
      return startSegment;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const slotHeight = rect.height / span;
    const index = Math.min(span - 1, Math.max(0, Math.floor((event.clientY - rect.top) / slotHeight)));
    if (index === 0) {
      return startSegment;
    }

    const keys = Object.keys(state.segments ?? {});
    const startIndex = keys.findIndex(key =>
      state.segments?.[key]?.some(item => item.location_id === location.id && item.id === startSegment.id),
    );
    if (startIndex < 0) {
      return startSegment;
    }

    return state.segments?.[keys[startIndex + index]]?.find(item => item.location_id === location.id) ?? startSegment;
  }

  function blockedRunIncludes(startSegment: ISegment, location: ILocation, span: number, candidate: ISegment) {
    const keys = Object.keys(state.segments ?? {});
    const startIndex = keys.findIndex(key =>
      state.segments?.[key]?.some(item => item.location_id === location.id && item.id === startSegment.id),
    );
    if (startIndex < 0) {
      return false;
    }

    for (let offset = 0; offset < span; offset++) {
      const item = state.segments?.[keys[startIndex + offset]]?.find(segment => segment.location_id === location.id);
      if (item?.id === candidate.id) {
        return true;
      }
    }

    return false;
  }

  function getSegmentSpan(segment: ISegment, location: ILocation, segmentKey: string) {
    const keys = Object.keys(state.segments ?? {});
    const index = keys.indexOf(segmentKey);
    const previous = index > 0
      ? state.segments[keys[index - 1]]?.find(item => item.location_id === location.id)
      : undefined;

    if (segmentsShareRun(segment, previous)) {
      return { skip: true, span: 0 };
    }

    let span = 1;
    for (let nextIndex = index + 1; nextIndex < keys.length; nextIndex++) {
      const next = state.segments[keys[nextIndex]]?.find(item => item.location_id === location.id);
      if (!segmentsShareRun(segment, next)) {
        break;
      }
      span += 1;
    }

    return { skip: false, span };
  }

  function segmentAtOffset(startSegment: ISegment, location: ILocation, offset: number) {
    if (offset <= 0) {
      return startSegment;
    }

    const keys = Object.keys(state.segments ?? {});
    const startIndex = keys.findIndex(key =>
      state.segments?.[key]?.some(item => item.location_id === location.id && item.id === startSegment.id),
    );
    if (startIndex < 0) {
      return startSegment;
    }

    return state.segments?.[keys[startIndex + offset]]?.find(item => item.location_id === location.id) ?? startSegment;
  }

  function eventEdges(segment: ISegment, location: ILocation, segmentKey: string, span: number) {
    if (!segment.league_id && !segment.tournament_id) {
      return "";
    }

    const keys = Object.keys(state.segments ?? {});
    const index = keys.indexOf(segmentKey);
    const previous = index > 0
      ? state.segments[keys[index - 1]]?.find(item => item.location_id === location.id)
      : undefined;
    const following = state.segments[keys[index + span]]?.find(item => item.location_id === location.id);
    const isStart = !sameScheduleEvent(segment, previous);
    const isEnd = !sameScheduleEvent(segment, following);

    return classNames("segment-slot--event", {
      "segment-slot--event-start": isStart,
      "segment-slot--event-end": isEnd,
      "segment-slot--event-joined": !(isStart && isEnd),
    });
  }

  function countLocationBookings(locationId: number) {
    const bookingIds = new Set<number>();
    Object.keys(state.segments ?? {}).forEach(segmentKey => {
      const segment = state.segments[segmentKey]?.find(item => item.location_id === locationId);
      if (segment?.blocked_type === "booked" && segment.booking_id && bookingMatchesQuery(segment, searchQuery)) {
        bookingIds.add(segment.booking_id);
      }
    });
    return bookingIds.size;
  }

  function openNewBooking() {
    const segmentKeys = Object.keys(state.segments ?? {});
    for (const segmentKey of segmentKeys) {
      for (const location of state.locations ?? []) {
        const segment = state.segments[segmentKey]?.find(item => item.location_id === location.id);
        if (segment?.blocked_type === "open" && !segment.booking_id && !segment.league_id && !segment.tournament_id) {
          void openBookingModal(segment, location);
          return;
        }
      }
    }
    uiActions.showError(t("secure.facility.reservation.reservation.007"));
  }

  async function setBlockedType(segment: ISegment, type: string) {
    const changedSegments: ISegment[] = [];
    const changedSegment = { ...segment };

    changedSegment.blocked_type = type;
    changedSegments.push(changedSegment);

    const segmentRes = await PutReservationSegment({ segments: changedSegments }, true);

    if (segmentRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation.023"));
      return;
    }

    void loadSegments();
  }

  async function updateWebBooking(segment: ISegment) {
    const changedSegments: ISegment[] = [];
    const changedSegment = { ...segment };

    changedSegment.web_booking_enabled = !segment.web_booking_enabled;
    changedSegments.push(changedSegment);

    const segmentRes = await PutReservationSegment({ segments: changedSegments }, true);

    if (segmentRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation.024"));
      return;
    }

    void loadSegments();
  }
  async function updateCreditCardRequired(segment: ISegment) {
    const changedSegments: ISegment[] = [];
    const changedSegment = { ...segment };

    changedSegment.credit_card_required = !segment.credit_card_required;
    changedSegments.push(changedSegment);

    const segmentRes = await PutReservationSegment({ segments: changedSegments }, true);

    if (segmentRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation.025"));
      return;
    }

    void loadSegments();
  }

  function handleShowDeletePopup(segment: any) {
    setState(state => ({
      ...state,
      deleteSegmentPopupVisible: true,
      segmentId: segment.id,
    }));
  }

  async function deleteSegment() {
    const deleteRes = await DeleteReservationSegment({ segment_ids: [state.segmentId] }, true);

    if (deleteRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation.026"));
      return;
    }

    void loadSegments();
    setState(state => ({
      ...state,
      deleteSegmentPopupVisible: false,
      segmentId: null,
    }));
  }

  function setDraggingBookingId(bookingId: number) {
    setState(state => ({
      ...state,
      draggingBookingId: bookingId,
    }));
  }

  function getAvailableDropHoursOfDropSegment(dropSegment: ISegment, dropLocation: ILocation) {
    let availableDropMinutes = 0;
    let reachedDropSegment = false;

    if (!dropSegment || !dropLocation) {
      return 0;
    } else {
      for (const segmentKey in state.segments) {
        const segment: ISegment = state.segments[segmentKey]?.find(
          searchSegment => searchSegment?.location_id === dropLocation?.id,
        );

        if (!segment) {
          continue;
        }

        if (segment?.id === dropSegment.id) {
          reachedDropSegment = true;
        }

        if (reachedDropSegment) {
          if (
            segment.blocked_type !== "open" &&
            !(segment.blocked_type === "booked" && segment.booking_id === state.draggingBookingId)
          ) {
            break;
          } else {
            availableDropMinutes += MINUTES_PER_SEGMENT;
          }
        }
      }
    }

    const availableDropHours = availableDropMinutes / 60;

    return availableDropHours;
  }

  function getStartingBookedSegment(bookedSegment: ISegment, location: ILocation) {
    if (bookedSegment?.blocked_type !== "booked" || !bookedSegment?.booking || !state?.segments) {
      return undefined;
    } else {
      for (const segmentKey in state.segments) {
        const segment: ISegment = state.segments[segmentKey]?.find(
          searchSegment => searchSegment?.location_id === location?.id,
        );

        if (segment?.booking?.id === bookedSegment.booking.id) {
          return segment;
        }
      }

      return undefined;
    }
  }

  function getBookedHoursOfBookedSegment(bookedSegment: ISegment, location: ILocation) {
    let bookedMinutes = 0;

    if (bookedSegment?.blocked_type !== "booked" || !bookedSegment?.booking || !state?.segments) {
      return 0;
    } else {
      for (const segmentKey in state.segments) {
        const segment: ISegment = state.segments[segmentKey]?.find(
          searchSegment => searchSegment?.location_id === location?.id,
        );

        if (segment?.booking?.id === bookedSegment.booking.id) {
          bookedMinutes += MINUTES_PER_SEGMENT;
        }
      }

      const bookedHours = bookedMinutes / 60;

      return bookedHours;
    }
  }

  function getDropSegmentIdsToMove(dropSegment: ISegment, dropLocation: ILocation, bookedHours: number) {
    let bookedMinutes = bookedHours * 60;
    let dropSegmentReached = false;
    const dropSegmentIds = [];

    if (!dropSegment || !dropLocation || !bookedHours) {
      return [];
    } else {
      for (const segmentKey in state.segments) {
        const segment: ISegment = state.segments[segmentKey]?.find(
          searchSegment => searchSegment?.location_id === dropLocation?.id,
        );

        if (segment?.id === dropSegment.id) {
          dropSegmentReached = true;
        }

        if (dropSegmentReached) {
          bookedMinutes -= MINUTES_PER_SEGMENT;
          dropSegmentIds.push(segment?.id);

          if (bookedMinutes <= 0) {
            break;
          }
        }
      }
      return dropSegmentIds;
    }
  }

  async function moveBookingToDropSegment(
    dropSegment: ISegment,
    dropSegmentLocation: ILocation,
    bookedHours: number,
    bookingId: number,
  ) {
    const dropSegmentIds = getDropSegmentIdsToMove(dropSegment, dropSegmentLocation, bookedHours);

    uiActions.enqueue();

    const moveReservationBookingResponse = await MoveReservationBooking(
      {
        booking_id: bookingId,
        location_id: dropSegmentLocation?.id,
        date: dropSegment?.date,
        segment_ids: dropSegmentIds,
      },
      false,
    );

    if (moveReservationBookingResponse.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation.041"));
      uiActions.dequeue();
      return;
    }

    await loadSegments(false);

    uiActions.dequeue();
  }

  function viewAuditTrail(segmentId: number) {
    void loadSegmentAuditTrail(segmentId);
  }

  async function handlePrintReservation() {
    const formattedDate = formatDate(state.selectedDate);

    const params = {
      date: formattedDate,
      module_id: state.selectedModule.id,
    };

    const printRes = await GetPrintReservation(params, true);

    if (printRes.status === StatusCode.OK) {
      window.open().document.write(String(printRes.data));
    } else {
      uiActions.showError(t("secure.facility.reservation.reservation.042"));
      return;
    }
  }

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (state.selectedDate && state.selectedModule) {
      void loadReservationNotifications(state.selectedDate, source.token);
    }
    return () => source.cancel();
  }, [state.selectedDate, state.selectedModule]);

  async function loadReservationNotifications(date: Date, token?: CancelToken) {
    teeSheetToast.show([]);

    const formattedDate: string = formatDate(date);

    const params = {
      date: formattedDate,
      user_type: "admin" as const,
      module_id: state.selectedModule?.id,
    };

    const notificationRes = await GetReservationNotificationAll(params, token ? false : true, token);

    if (token && token.reason) {
      return;
    }

    if (notificationRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reservation.reservation.043")));
      return;
    }

    if (notificationRes.status === StatusCode.OK && notificationRes.data?.length !== 0) {
      const toastList: Array<{
        subject: string;
        content: string;
        duration?: number;
        onClose?: () => void;
      }> = notificationRes.data?.map((data: any) => {
        let duration = 10000;
        if (data.type !== "passive") {
          duration = null;
        }
        return {
          subject: data.subject,
          content: data.content,
          duration: duration,
        };
      });

      teeSheetToast.show(toastList);
    }

    setState(prevState => ({ ...prevState, notifications: notificationRes.data }));
  }

  async function navigateToTable(tableId?: number) {
    const tableIdToView = tableId ? tableId : openTableModal?.tableId;
    if (tableIdToView) {
      if (tableId && !authStore?.user?.permissions?.table_service_table_override) {
        const res = await GetTable({id: tableId}, true);
        if (res?.status !== StatusCode.OK || (res?.data && res?.data?.length === 0)) {
          dispatch(showError(t("Table not found")));
          return;
        }
        if (res?.data[0]?.user_id !== authStore?.user?.id) {
          dispatch(showError(t("You do not have access to this table")));
          return;
        }
      }
      await dispatch(loadActiveTableNew(tableIdToView, true));
      history.push("/admin/table-service");
    }
  }

  async function handleOpenTableCheckIn() {
    const register = JSON.parse(localStorage.getItem("register"));
    if (!tableSelectionModal?.tableId) {
      dispatch(showError(t("Table selection required")));
      return;
    } else if (!tableSelectionModal?.guestsAmount || tableSelectionModal?.guestsAmount < 0) {
      dispatch(showError(t("Number of seats selected must be greater than 0")));
      return;
    }

    if (register) {
      const tableId = tableSelectionModal?.tableId;
      dispatch(enqueue());
      const openTableRes = await PutOpenTable(
        {
          register_id: register?.id,
          table_id: tableSelectionModal?.tableId,
          number_of_seats: tableSelectionModal?.guestsAmount,
          reservation_customer_id: tableSelectionModal?.segment?.booking?.customer_id,
        },
        false,
      );

      if (openTableRes.status !== StatusCode.OK) {
        dispatch(dequeue());
        uiActions.showError(t("Error opening table"));
        return;
      }

      const firstCart = openTableRes?.data?.carts[0];
      const lineItemVariants: Array<{ variant_id: number; quantity: number }> = [];
      const seenVariantIds: Array<number> = [];
      (tableSelectionModal?.segment?.blocks ?? []).forEach(block => {
        const variantId = block?.product_variant_id;
        if (variantId == null || seenVariantIds.indexOf(variantId) !== -1) {
          return;
        }
        seenVariantIds.push(variantId);
        lineItemVariants.push({ variant_id: variantId, quantity: 1 });
      });
      if (firstCart && lineItemVariants.length > 0) {
        const lineItemRes = await PostLineItemToCart({ cart_id: firstCart?.id, variants: lineItemVariants }, false);
        if (lineItemRes?.status !== StatusCode.OK) {
          dispatch(dequeue());
          uiActions.showError(t("Error adding line item to cart"));
          return;
        }
      }

      const bookingRes = await PutReservationBooking(
        {
          id: tableSelectionModal?.segment?.booking_id,
          check_in_status: "checked",
          table_id: tableSelectionModal?.tableId,
        },
        false,
      );

      if (bookingRes?.status !== StatusCode.OK) {
        dispatch(dequeue());
        dispatch(showError(t("Error checking in customer")));
        return;
      }
      dispatch(dequeue());

      uiActions.showSuccess(t("Successfully opened table and checked in customer"));
      void closeTableSelectionModal();
      void updateOpenTableModal({isOpen: true, tableId: tableId});
      void loadSegments();
    } else {
      uiActions.showError(t("Select a register to open a table"));
    }
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="facility-reservation">
        <div
          className={classNames("facility-reservation-content", {
            "facility-reservation-content--booking-open": state.editBookingActive,
          })}
        >
          <Page full className="reservation-page">
            {hasModules && (
              <>
                <div className="reservation-toolbar">
                  <div className="reservation-module" ref={moduleMenuRef}>
                    <button
                      type="button"
                      className="reservation-module-button"
                      onClick={() => setModuleMenuOpen(open => !open)}
                    >
                      {state.selectedModule?.title}
                      <FontAwesomeIcon icon={faChevronDown} />
                    </button>
                    {moduleMenuOpen && (
                      <div className="reservation-module-menu">
                        {state.modules.map(module => (
                          <button
                            key={module.id}
                            type="button"
                            className="reservation-menu-item"
                            onClick={() => {
                              handleFilterMenuChange(module);
                              setModuleMenuOpen(false);
                            }}
                          >
                            {module.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="reservation-toolbar-dates">
                    <DatePickerInput
                      months={1}
                      position="left"
                      startingDate={state.selectedDate}
                      setStartingDate={date => {
                        if (date instanceof Date) {
                          handleDateChange(date);
                        }
                      }}
                      iconOnlyTrigger
                    />
                    <div className="reservation-toolbar-date-bar">
                      <DateBar
                        numberOfDays={7}
                        selectedDate={state.selectedDate}
                        onClick={handleDateChange}
                        showNavigation
                        keepToday
                      />
                    </div>
                  </div>
                  <div className="reservation-toolbar-actions">
                    <ButtonNew
                      className="reservation-new-booking"
                      type="primary"
                      size="medium"
                      disabled={!state.segments}
                      onClick={openNewBooking}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      {t("New booking")}
                    </ButtonNew>
                    <div className="reservation-actions" ref={actionsMenuRef}>
                      <button
                        type="button"
                        className="reservation-actions-button"
                        aria-label={t("secure.facility.reservation.reservation.044")}
                        onClick={() => setActionsMenuOpen(open => !open)}
                      >
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                      </button>
                      {actionsMenuOpen && (
                        <div className="reservation-actions-menu">
                          <button
                            type="button"
                            className="reservation-menu-item"
                            onClick={() => {
                              setActionsMenuOpen(false);
                              void handlePrintReservation();
                            }}
                          >
                            <FontAwesomeIcon icon={faPrint} />
                            {t("Print sheet")}
                          </button>
                          <button
                            type="button"
                            className="reservation-menu-item"
                            onClick={() => {
                              setActionsMenuOpen(false);
                              handleOpenEmailModal();
                            }}
                          >
                            <FontAwesomeIcon icon={faEnvelope} />
                            {t("Send email")}
                          </button>
                          <button
                            type="button"
                            className="reservation-menu-item"
                            onClick={() => {
                              setActionsMenuOpen(false);
                              toSegmentsEditPage();
                            }}
                          >
                            <FontAwesomeIcon icon={faPen} />
                            {t("Edit Template")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={classNames("reservation-filters", { "reservation-filters--search-only": !locationGroups })}>
                  {locationGroups && (
                    <div className="reservation-location-groups">
                      <span className="reservation-location-groups-label">
                        {unitLabel(state.selectedModule?.title, t("Location"))}:
                      </span>
                      <ButtonGroup
                        startKey={String(activeLocationGroupIndex)}
                        onButtonClick={key => scrollToLocationGroup(Number(key))}
                        buttons={locationGroups.map((group, index) => ({
                          key: String(index),
                          label: locationGroupLabel(group),
                        }))}
                      />
                    </div>
                  )}
                  <label className="reservation-search">
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                    <input
                      type="search"
                      value={searchQuery}
                      placeholder={t("Search booking or player...")}
                      onChange={event => setSearchQuery(event.target.value)}
                    />
                  </label>
                </div>
              </>
            )}
            {!hasModules ? (
              <Callout
                actions={{
                  primary: {
                    label: t("Manage Activity Types"),
                    onClick: navigateToModules,
                  },
                }}
                type="error"
                title={t("No Activity Types Created")}
                content={t("Create an activity type to start managing activities.")}
              />
            ) : state.selectedModule && state.locations && state.segments && Object.keys(state.segments)?.length > 0 ? (
              <div className="segments-container" ref={segmentsContainerRef}>
                <table
                  className={classNames("segments-table", {
                    "segments-table--scroll-locations": Boolean(locationColumnWidth),
                  })}
                  style={
                    locationColumnWidth
                      ? { width: SEGMENT_TIME_COLUMN_WIDTH + state.locations.length * locationColumnWidth }
                      : undefined
                  }
                >
                  <thead>
                    <tr>
                      <td className="segment-location-header segment-time" />
                      {state.locations.map(location => {
                        const bookingCount = countLocationBookings(location.id);
                        return (
                          <td
                            key={location.id}
                            className="segment-location-header"
                            style={locationColumnWidth ? { width: locationColumnWidth } : undefined}
                          >
                            <div className="segment-location-heading">
                              <span>{location.title}</span>
                              <span className="segment-location-count">
                                {bookingCount} {t(bookingCount === 1 ? "booking" : "bookings")}
                              </span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(state?.segments)?.map((segmentKey, segmentKeyIndex) => {
                      const slotTime = moment(segmentKey, ["HH:mm:ss", "hh:mm:ss"]);
                      return (
                        <tr key={segmentKeyIndex}>
                          <td className={classNames("segment-time", slotTime.minutes() === 0 ? "segment-time--hour" : "segment-time--half")}>
                            {slotTime.format("hh:mm A")}
                          </td>
                          {state.locations?.map(location => {
                            const segment = state?.segments[segmentKey]?.find(
                              searchSegment => searchSegment?.location_id === location?.id,
                            );
                            if (!segment) {
                              return <td key={location.id} className="segment-slot segment-slot--unavailable" />;
                            }
                            const segmentSpan = getSegmentSpan(segment, location, segmentKey);
                            if (segmentSpan.skip) {
                              return null;
                            }
                            if (segment?.blocked_type === "open" && segment?.booking_id === null) {
                              const isScheduleBand = !!segment.league_id || !!segment.tournament_id;
                              const hasEventBooking = isScheduleBand && eventHasBooking(state.segments, segment, location.id);
                              const bounds = isScheduleBand && !hasEventBooking
                                ? eventBounds(state.segments, segment, location.id, segmentKey)
                                : undefined;
                              return (
                                <OpenSegment
                                  key={location.id}
                                  rowSpan={segmentSpan.span}
                                  dimmed={searchQuery.trim().length > 0 && isScheduleBand}
                                  slotFlags={slotFlagsForSpan(state.segments, location.id, segmentKey, segmentSpan.span)}
                                  band={isScheduleBand && !hasEventBooking ? {
                                    kind: segment.league_id ? "league" : "tournament",
                                    title: segment.league_id
                                      ? segment.league_name || t("secure.facility.reservation.reservation.013")
                                      : segment.tournament_name || t("secure.facility.reservation.reservation.014"),
                                    tag: segment.league_id
                                      ? t("secure.facility.reservation.reservation.013")
                                      : t("secure.facility.reservation.reservation.014"),
                                    timeLabel: bounds?.timeLabel || formatSlotRange(segment.start_time, segmentSpan.span),
                                  } : undefined}
                                  onSelectSlot={index =>
                                    void openBookingModal(segmentAtOffset(segment, location, index), location)
                                  }
                                  cellClassNames={classNames(
                                    "segment-slot",
                                    isScheduleBand ? "segment-slot--band" : "segment-slot--open",
                                    eventEdges(segment, location, segmentKey, segmentSpan.span),
                                  )}
                                  cellContentClassName="segment-slot-content"
                                  segment={segment}
                                  location={location}
                                  availableDropHours={getAvailableDropHoursOfDropSegment(segment, location)}
                                  moveBookingToDropSegment={moveBookingToDropSegment}
                                  openBookingModal={openBookingModal}
                                  setBlockedType={setBlockedType}
                                  updateCreditCardRequired={updateCreditCardRequired}
                                  updateWebBooking={updateWebBooking}
                                  deleteSegment={handleShowDeletePopup}
                                  viewAuditTrail={viewAuditTrail}
                                />
                              );
                            } else if (segment?.blocked_type === "open_reserved") {
                              return (
                                <td key={location.id} className="segment-slot segment-slot--reserved">
                                  <div className="segment-right-click-menu">
                                    <RightClickMenu
                                      fullHeightWidth
                                      sections={[
                                        [
                                          {
                                            icon: "circle",
                                            title: t("secure.facility.reservation.reservation.027"),

                                            action: () => void setBlockedType(segment, "open"),
                                            disabled: String(segment.blocked_type) === "open",
                                          },
                                          {
                                            icon: "ban",
                                            title: t("secure.facility.reservation.reservation.028"),
                                            action: () => void setBlockedType(segment, "blocked"),

                                            disabled: String(segment.blocked_type) === "blocked",
                                          },
                                        ],
                                        [
                                          {
                                            icon: "globe",
                                            title: segment.web_booking_enabled
                                              ? t("secure.facility.reservation.reservation.029")
                                              : t("secure.facility.reservation.reservation.030"),
                                            action: () => void updateWebBooking(segment),
                                          },
                                          {
                                            icon: "credit-card",
                                            title: segment.credit_card_required
                                              ? t("secure.facility.reservation.reservation.031")
                                              : t("secure.facility.reservation.reservation.032"),
                                            action: () => void updateCreditCardRequired(segment),
                                          },
                                        ],
                                        [
                                          {
                                            icon: "trash",
                                            title: t("secure.facility.reservation.reservation.033"),
                                            action: () => handleShowDeletePopup(segment),
                                          },
                                        ],
                                      ]}
                                    >
                                      <div className="segment-slot-label">{capitalize(segment?.blocked_type)}</div>
                                    </RightClickMenu>
                                    <SegmentSlotFlags flags={slotFlagsForSpan(state.segments, location.id, segmentKey, 1)} />
                                  </div>
                                </td>
                              );
                            } else if (segment?.blocked_type === "blocked") {
                              const menuSegment =
                                blockedMenuSegment?.location_id === location.id &&
                                blockedRunIncludes(segment, location, segmentSpan.span, blockedMenuSegment)
                                  ? blockedMenuSegment
                                  : segment;
                              return (
                                <td
                                  key={location.id}
                                  rowSpan={segmentSpan.span}
                                  className={classNames("segment-slot segment-slot--blocked", {
                                    "segment-slot--span": segmentSpan.span > 1,
                                  }, eventEdges(segment, location, segmentKey, segmentSpan.span))}
                                  onPointerDown={event => {
                                    if (event.button !== 0 && event.button !== 2) {
                                      return;
                                    }
                                    const target = blockedSegmentFromPointer(event, location, segment, segmentSpan.span);
                                    blockedMenuSegmentRef.current = target;
                                    if (event.button === 2 || event.pointerType === "touch") {
                                      setBlockedMenuSegment(target);
                                    }
                                  }}
                                >
                                  <div className="segment-booking-frame" style={{ height: segmentSpan.span * 52 }}>
                                    <RightClickMenu
                                      fullHeightWidth
                                      sections={[
                                        [
                                          {
                                            icon: "circle",
                                            title: t("secure.facility.reservation.reservation.027"),
                                            action: () => void setBlockedType(blockedMenuSegmentRef.current ?? menuSegment, "open"),
                                            disabled: String(menuSegment.blocked_type) === "open",
                                          },
                                          {
                                            icon: "ban",
                                            title: t("secure.facility.reservation.reservation.028"),
                                            action: () => void setBlockedType(blockedMenuSegmentRef.current ?? menuSegment, "blocked"),
                                            disabled: String(menuSegment.blocked_type) === "blocked",
                                          },
                                        ],
                                        [
                                          {
                                            icon: "globe",
                                            title: menuSegment.web_booking_enabled
                                              ? t("secure.facility.reservation.reservation.029")
                                              : t("secure.facility.reservation.reservation.030"),
                                            action: () => void updateWebBooking(blockedMenuSegmentRef.current ?? menuSegment),
                                          },
                                          {
                                            icon: "credit-card",
                                            title: menuSegment.credit_card_required
                                              ? t("secure.facility.reservation.reservation.031")
                                              : t("secure.facility.reservation.reservation.032"),
                                            action: () => void updateCreditCardRequired(blockedMenuSegmentRef.current ?? menuSegment),
                                          },
                                        ],
                                        [
                                          {
                                            icon: "trash",
                                            title: t("secure.facility.reservation.reservation.033"),
                                            action: () => handleShowDeletePopup(blockedMenuSegmentRef.current ?? menuSegment),
                                          },
                                        ],
                                      ]}
                                    >
                                      <div className="segment-slot-content">
                                        <div
                                          className={classNames("blocked-segment", {
                                            "blocked-segment--compact": segmentSpan.span <= 1,
                                            "blocked-segment--dimmed": searchQuery.trim().length > 0,
                                          })}
                                        >
                                          <div className="blocked-segment-heading">
                                            <FontAwesomeIcon icon={faBan} className="blocked-segment-icon" />
                                            <span>{t("Blocked")}</span>
                                          </div>
                                          <p className="blocked-segment-time">
                                            {formatSlotRange(segment.start_time, segmentSpan.span)}
                                          </p>
                                        </div>
                                      </div>
                                    </RightClickMenu>
                                    <SegmentSlotFlags flags={slotFlagsForSpan(state.segments, location.id, segmentKey, segmentSpan.span)} />
                                  </div>
                                </td>
                              );
                            } else if (segment?.blocked_type === "booked" && segment?.booking_id) {
                              return (
                                <BookedSegment
                                  key={location.id}
                                  rowSpan={segmentSpan.span}
                                  dimmed={!bookingMatchesQuery(segment, searchQuery)}
                                  cellClassNames={classNames(
                                    "segment-slot segment-slot--booked",
                                    eventEdges(segment, location, segmentKey, segmentSpan.span),
                                  )}
                                  cellContentClassNames="segment-slot-content"
                                  segment={segment}
                                  startingSegment={getStartingBookedSegment(segment, location)}
                                  location={location}
                                  availableDropHours={getAvailableDropHoursOfDropSegment(segment, location)}
                                  bookedHours={getBookedHoursOfBookedSegment(segment, location)}
                                  draggingBookingId={state.draggingBookingId}
                                  setDraggingBookingId={setDraggingBookingId}
                                  moveBookingToDropSegment={moveBookingToDropSegment}
                                  openEditBookingModal={openEditBookingModal}
                                  updateCheckInStatus={updateCheckInStatus}
                                  updateFinancialStatus={updateFinancialStatus}
                                  viewAuditTrail={viewAuditTrail}
                                  setBlockedType={setBlockedType}
                                  openTableSelectionModal={segment => updateTableSelectionModal({isOpen: true, segment, guestsAmount: segment?.booking?.quantity})}
                                  openViewTableModal={tableId => navigateToTable(tableId)}
                                  acceptBooking={segment =>
                                    updateApprovalModal({ isOpen: true, segment, action: "approve" })
                                  }
                                  declineBooking={segment =>
                                    updateApprovalModal({ isOpen: true, segment, action: "decline" })
                                  }
                                />
                              );
                            } else {
                              return <td key={location.id} className="segment-slot segment-slot--unavailable" />;
                            }
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <Callout
                actions={{
                  primary: {
                    label: t("secure.facility.reservation.reservation.034"),
                    onClick: navigateToSegmentConfiguration,
                  },
                }} //Translations Moises
                type="error"
                title={t("secure.facility.reservation.reservation.015")}
                content={t("secure.facility.reservation.reservation.016")}
              />
            )}
          </Page>
          <ReservationBookingModalEdit
            uiActions={uiActions}
            authStore={authStore}
            date={state.selectedDate}
            module={state.selectedModule}
            segment={state.selectedSegment}
            location={state.selectedLocation}
            locations={state.locations}
            open={state.editBookingActive}
            durations={state.durations}
            durationKey={state.durationKey}
            onSave={useGlobalLoader => void loadSegments(useGlobalLoader)}
            onCancel={closeEditBooking}
            onOpenTableSuccess={tableId => updateOpenTableModal({isOpen: true, tableId})}
            booking={state?.booking}
            segments={state.bookingSegments}
          />
        </div>
      </div>

      <ReservationBookingModalNew
        uiActions={uiActions}
        authStore={authStore}
        date={state.selectedDate}
        module={state.selectedModule}
        selectedSegment={state.selectedSegment}
        selectedLocation={state.selectedLocation}
        segments={state.bookingSegments}
        locations={state.locations}
        onSave={() => void loadSegments()}
        onCancel={closeNewBooking}
        open={state.newBookingActive}
      />

      <Sheet
        closable
        onCancel={() => setEmailState(prevState => ({ ...prevState, modalVisible: false }))}
        size="medium"
        open={emailState.modalVisible}
        cancelText={t("Close")}
        okText={t("Send Email")}
        title={t("Email")}
        onOk={() => emailBookings()}
      >
        <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange}>
          {/* Booking Details */}
          {selected === 0 ? (
            <div className="mt-4 flex flex-col gap-2">
              <Input
                type="text"
                label={t("Subject")}
                onChange={handleChangeEmailSubject}
                value={emailState.subject}
              />
              <TextEditor
                label={t("Content")}
                markdownText={emailState.content ?? ""}
                markdownTextOnChange={handleChangeEmailContent}
              />
            </div>
          ) : null}
          {selected === 1 ? (
            <Form>
              <FormLayout>
                <FormLayout.Group>
                  <div className="mt-4 mb-1">
                    <Checkbox
                      id={"select-all"}
                      size="medium"
                      checked={emailState.selectAllRecipients}
                      onChange={handleSelectAllCheckboxChange}
                      label={t("secure.facility.reservation.reservation.021")}
                    />
                  </div>
                </FormLayout.Group>
                {!emailState.selectAllRecipients && (
                  <>
                    <FormLayout.Group>
                      <span>{t("secure.facility.reservation.reservation.022")}</span>
                    </FormLayout.Group>
                    <FormLayout.Group>
                      <div>
                        {state.uniqueBookedSegments.map((recipient, i) => {
                          return (
                            <div key={i}>
                              <Checkbox
                                id={String(recipient.booking.id)}
                                size="large"
                                checked={recipient.checked}
                                onChange={e => handleCheckboxChange(e, recipient)}
                                label={`${moment(recipient.start_time, "hh:mm:ss").format("h:mm A")} (${
                                  recipient.booking.customer.full_name
                                })${recipient.booking.status === "pending" ? ` — ${t("Pending")}` : ""}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </FormLayout.Group>
                  </>
                )}
              </FormLayout>
            </Form>
          ) : null}
        </Tabs>
      </Sheet>

      <Sheet
        open={state.auditTrailModalVisible}
        size="medium"
        closable
        title={t("secure.facility.reservation.reservation.047")}
        onCancel={() => setState(state => ({ ...state, auditTrailModalVisible: false }))}
        onOk={""}
        cancelText={t("Close")}
        okText={t("Submit")}
        overflow
      >
        {!state.auditTrailsLoading ? (
          <div>
            {state.auditTrails?.length > 0 ? (
              state.auditTrails.map((audit: any, index: number) => {
                return (
                  <React.Fragment key={index}>
                    <AuditTrailNote
                      title={audit.title}
                      author={audit.author}
                      actions={audit.actions}
                      action={audit.action}
                      timestamp={audit.created_at}
                      last={index + 1 === state.auditTrails.length ? true : false}
                    />
                  </React.Fragment>
                );
              })
            ) : (
              <span>{t("secure.facility.reservation.reservation.048")}</span>
            )}
          </div>
        ) : (
          <div style={{ height: "25px", width: "25px" }}>
            <Spin />
          </div>
        )}
      </Sheet>

      <Popup
        open={state.deleteSegmentPopupVisible}
        type={"warning"}
        title={t("secure.facility.reservation.reservation.035")}
        description={t("secure.facility.reservation.reservation.036")}
        closable
        cancelText={t("secure.facility.reservation.reservation.037")}
        okText={t("secure.facility.reservation.reservation.038")}
        onCancel={() => setState(prevState => ({ ...prevState, deleteSegmentPopupVisible: false }))}
        onOk={deleteSegment}
      />

      <Popup
        open={approvalModal.isOpen}
        type={approvalModal.action === "decline" ? "warning" : "info"}
        title={
          approvalModal.action === "decline" ? t("Decline Booking Request") : t("Approve Booking Request")
        }
        description={
          approvalModal.action === "decline"
            ? t("This will decline the customer's reservation request and release the time.")
            : t("This will confirm the reservation and send the customer a confirmation email.")
        }
        closable
        cancelText={t("Cancel")}
        okText={approvalModal.action === "decline" ? t("Decline Request") : t("Approve Booking")}
        onOkType={approvalModal.action === "decline" ? "error" : "primary"}
        onCancel={closeApprovalModal}
        onOk={() =>
          approvalModal.action === "decline"
            ? void declineBooking(approvalModal.segment)
            : void acceptBooking(approvalModal.segment)
        }
      />

    {/* Open Table & Check In */}
    <Portal isMounted={tableSelectionModal?.isOpen}>
        <Sheet
        open={tableSelectionModal?.isOpen}
        title={t("Check In and Open Table")}
        size="medium"
        okText={t("Open Table")}
        onCancel={closeTableSelectionModal}
        onOk={handleOpenTableCheckIn}
        okDisabled={!tableSelectionModal.tableId || !tableSelectionModal.guestsAmount}
        >
            <div>
              <ReservationTableSelection 
              guestsAmount={tableSelectionModal.guestsAmount} 
              onTableSelect={(tableId, seatsAmount) => updateTableSelectionModal({tableId, guestsAmount: seatsAmount})} 
              selectedTableId={tableSelectionModal?.tableId}
              />
            </div>
        </Sheet>
    </Portal>


      <Portal isMounted={openTableModal?.isOpen}>
        <Popup
          open={openTableModal?.isOpen}
          type={"success"}
          title={t("Table Opened Successfully")}
          description={t("Table opened and user checked in successfully")}
          closable
          okText={t("View Table")}
          onCancel={closeOpenTableModal}
          onOk={() => navigateToTable()}
        />
      </Portal>
    </DndProvider>
  );
}
